// #region Libraries
import { useCallback, useContext, useState, useEffect, Fragment } from "react";
import { useNavigate, useParams } from "react-router";
import { Modal, ModalBody } from '@/components/bootstrap/TemporaryReactstrapCompatibilityLayer';
// #endregion

// #region Components
import Button from "../../layout/Button";
import StatusBarButton from "../../layout/StatusBarButton";
import AutoComplete from "../../container-management/AutoComplete";
import GoogleLocationMap from "../../container-management/GoogleLocationMap";
import Loading from "../../ui-elements/Loading";
// #endregion

// #region Contexts
import HarvestingProceduresContext from "../../../contexts/management/harvestingProcedures/HarvestingProceduresContext";
import ProcessContext from "../../../contexts/process/ProcessContext";
import UIContext from "../../../contexts/ui/UIContext";
import AuthContext from "../../../contexts/auth/AuthContext";
// #endregion

// #region Services
import HarvestingProceduresApiService from "../../../services/apiServices/HarvestingProceduresApiService";
import ContainersApiService from "../../../services/apiServices/ContainersApiService";
import FarmhandInstallationsApiService from "../../../services/apiServices/FarmhandInstallationsApiService";
// #endregion

// #region Utilities
import { separateOnCapitalLetters } from "../../../utility/PascalCaseSeparator";
import { MULTI_HARVEST_CUT, SINGLE_HARVEST_CUT, SINGLE_HARVEST_ROOTED, harvestingProcedureTypes } from "../../../constants/harvestingProcedures";
import { HELP_DESK_LINK } from "../../../constants/helpDesk";
import DevicesApiService from "../../../services/apiServices/DevicesApiService";
import Devices from "./Device";
import { geocodeLatLng } from "../../../services/googleMapFactory";
// #endregion

/**
  * `Edit` is a React component used to facilitate the update of container data.
 *
 * @component
 * @example
 * return <Create />
 */
const Edit = () => {
  // #region Context properties
  const navigate = useNavigate();
  const { containerId } = useParams();
  const { accessibleContainers, currentTenant, setAccessibleContainers } = useContext(AuthContext);
  const { notifyError, notifySuccess } = useContext(ProcessContext);
  const [containerHarvestingProcedures, setContainerHarvestingProcedures] = useState();
  const { setInfo } = useContext(UIContext);
  const container = accessibleContainers.find(c => c.id === containerId);
  const { harvestingProceduresLoading } = useContext(HarvestingProceduresContext);
  // #endregion

  // #region Methods
  const getRootedAdvice = () => {
    if (containerHarvestingProcedures) {
      const rootedProcedure = containerHarvestingProcedures?.find(procedure => procedure.type.includes('Rooted'));
      return rootedProcedure?.storageAdvice ?? '';
    }
  }

  const getCutAdvice = () => {
    if (containerHarvestingProcedures) {
      const cutProcedure = containerHarvestingProcedures?.find(p => p.type.includes("Cut")) ?? '';
      return cutProcedure?.storageAdvice ?? '';
    }
  }
  // #endregion

  // #region States
  const [probingFarmHand, setProbingFarmHand] = useState(false);
  const [first, setFirst] = useState(true);
  const [farmHandIsLoaded, setFarmHandIsLoaded] = useState(false);

  const cutAdvice = getCutAdvice() ?? '';
  const rootedAdvice = getRootedAdvice() ?? '';
  const [localLoading, setLocalLoading] = useState(false);
  const [saveDone, setSaveDone] = useState(false);

  const [locationString, setLocationString] = useState('');
  const [location, setLocation] = useState({
    lat: 50.9396911,
    lng: 6.8551075,
  });
  const [openLocation, setOpenLocation] = useState(false);
  const initialContainerState = {
    id: {
      value: container?.id ?? '',
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: '',
    },
    name: {
      value: container?.name ?? '',
      touched: false,
      validate: function (text) {
        return text?.length >= 3;
      },
      isValid: !!container?.name,
      placeholder: 'Container name ...',
    },
    description: {
      value: container?.description ?? '',
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: 'Container description ...',
      initialValue: container?.description ?? null,
    },
    farmHandName: {
      value: container?.farmHandName,
      touched: false,
      validate: async () => {
        return true;
      },
      isValid: !!container?.farmHandName,
      placeholder: '',
      initialValue: container?.farmHandName ?? ''
    },
    farmHandInstallationId: {
      value: container?.farmHandInstallationId,
      touched: false,
      validate: async () => {
        return true;
      },
      isValid: true,
      placeholder: '',
      initialValue: container?.farmHandInstallationId ?? ''
    },
    weighingCameraSettings: {
      isValid: true,
    },
    robotSettings: {
      isValid: true,
    },
    location: {
      value: {
        latitude: container?.latitude ?? null,
        longitude: container?.longitude ?? null,
        street: container?.street ?? '',
        number: container?.number ?? '',
        postalCode: container?.postalCode ?? '',
        city: container?.city ?? '',
        country: container?.country ?? ''
      },
      initialValue: {
        latitude: container?.location?.latitude ?? null,
        longitude: container?.location?.longitude ?? null,
        street: container?.location?.street ?? '',
        number: container?.location?.number ?? '',
        postalCode: container?.location?.postalCode ?? '',
        city: container?.location?.city ?? '',
        country: container?.location?.country ?? ''
      },
      validate: () => true
    },
    rootedAdvice: {
      value: rootedAdvice ?? '',
      touched: false,
      validate: () => {
        return true;
      },
      isValid: !!container?.farmHandName,
      placeholder: '',
      initialValue: rootedAdvice ?? '',
    },
    cutAdvice: {
      value: cutAdvice ?? '',
      touched: false,
      validate: () => {
        return true;
      },
      isValid: !!container?.farmHandName,
      placeholder: '',
      initialValue: cutAdvice ?? ''
    },
    weighingCameraId: {
      value: container?.weighingCameraId ?? '',
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: '',
    },
    cameras: {
      value: container?.cameras ?? [],
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: '',
    },
    labelPrinterId: {
      value: container?.labelPrinterId ?? '',
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: '',
    },
    scaleId: {
      value: container?.scaleId ?? '',
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: '',
    },
    robotId: {
      value: container?.robotId ?? '',
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: '',
    },
    tenantId: {
      value: container?.tenantId ?? '',
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: '',
    },
    isVirtual: {
      value: container?.isVirtual ?? false,
      touched: false,
      validate: () => true,
      isValid: true,
    },
    harvestingProcedures: containerHarvestingProcedures
  };
  const [containerState, setContainerState] = useState(initialContainerState);
  // #endregion

  // #region useEffect

  const initiallyLoadFarmHandName = async () => {
    if (!initialContainerState?.farmHandInstallationId?.value || !container) return;

    try {
      setLocalLoading(true);
      const result = await FarmhandInstallationsApiService.getFarmHandInstallationById(initialContainerState?.farmHandInstallationId?.value);
      const { id, name } = result?.data?.data ?? {};
      setContainerState((prev) => ({
        ...prev,
        farmHandName: {
          ...prev.farmHandName,
          value: name ?? '',
          initialValue: name ?? ''
        },
       farmHandInstallationId: {
          ...prev.farmHandInstallationId,
          value: id ?? '',
          initialValue: id ?? ''
        },
      }));
    } catch (error) {
      console.error("Unexpected error while loading farm hand installation.", error);
      notifyError(<>We have encountered an issue on our side while trying to load farmhand installations. You can still continue updating the container, you just can not update farmhand installations at this point. If the error still exists later, please contact {HELP_DESK_LINK}.</>);
    } finally {
      setLocalLoading(false);
      setFarmHandIsLoaded(true);
    }
  };

  useEffect(() => {
    if ((!containerState?.harvestingProcedures || containerState?.harvestingProcedures.length < 1) && containerHarvestingProcedures) {
      setContainerState((prev) => ({
        ...prev,
        ['harvestingProcedures']: containerHarvestingProcedures,
        ['rootedAdvice']: {
          ...containerState['rootedAdvice'],
          value: getRootedAdvice(),
          validate: () => true,
          initialValue: getRootedAdvice()
        },
        ['cutAdvice']: {
          ...containerState['cutAdvice'],
          value: getCutAdvice(),
          validate: () => true,
          initialValue: getCutAdvice()
        }
      }));
    }
  }, [containerHarvestingProcedures]);

  const getContainerHarvestingProcedures = async () => {
    var harvestingProcedures = await HarvestingProceduresApiService.getManyHarvestingProcedures(currentTenant?.id, containerId);
    setContainerHarvestingProcedures(harvestingProcedures.data.data);
    return harvestingProcedures.data.data;
  };

  useEffect(() => {
    setInfo(`Edit Container ${initialContainerState?.name?.value}`);
    const initialLoad = async () => {
      setLocalLoading(true);

      let containerStateInitTemp = Object.entries(container).reduce((acc, [key, value]) => {
        if (!(key in containerState)) {
          console.warn(`Key "${key}" fehlt im containerState.`);
          return acc;
        }
        if (typeof acc[key]?.validate !== 'function') {
          console.warn(`validate-Methode für "${key}" fehlt oder ist nicht definiert.`);
          return acc;
        }
        return {
          ...acc,
          [key]: {
            ...acc[key],
            value,
            isValid: acc[key]?.validate(value),
          },
        };
      }, initialContainerState);
      if (container) {
        if (container?.weighingCameraId) {
          const state = await DevicesApiService.getDeviceState(container?.tenantId, container?.id, container?.weighingCameraId);
          const weighingCameraSettings = {
            initialEnabled: state?.data.data == 1 ? true : false,
            enabled: state?.data.data == 1 ? true : false,
            isValid: true,
          };
          containerStateInitTemp.weighingCameraSettings = weighingCameraSettings;

        }

        if (container?.robotId) {
          const state = await DevicesApiService.getDeviceState(container?.tenantId, container?.id, container?.robotId);
          const robotSettings = {
            initialEnabled: state?.data?.data == 1 ? true : false,
            enabled: state?.data?.data == 1 ? true : false,
            isValid: true,
          };
          containerStateInitTemp.robotSettings = robotSettings;

        }

        setContainerState(containerStateInitTemp);
        await initiallyLoadFarmHandName();
        await getContainerHarvestingProcedures();

        var lat = containerStateInitTemp.location.value.latitude;
        var lng = containerStateInitTemp.location.value.longitude;
        setLocation({
          lat: lat ? Number(lat) : 50.9396911,
          lng: lng ? Number(lng) : 6.8551075,
        });
        setLocationString((containerStateInitTemp.location.value.latitude && containerStateInitTemp.location.value.longitude)? containerStateInitTemp.location.value.street + ' ' + containerStateInitTemp.location.value.number + ' ' + containerStateInitTemp.location.value.postalCode + ' ' + containerStateInitTemp.location.value.city + ' ' + containerStateInitTemp.location.value.country: '');}
      setLocalLoading(false);
    };


    initialLoad();

  }, []);

  useEffect(() => {
    if (first) {
      setFirst(false);
      return;
    }

    if (saveDone) {
      navigateToDetails();
    }

  }, [accessibleContainers, saveDone]);

  const isAllRequiredDataPresent = () => {
    return containerHarvestingProcedures && (farmHandIsLoaded || !initialContainerState?.farmHandInstallationId?.value);
  }

  // #endregion

  const analyzeHarvestingProceduresUpdate = () => {
    const harvestingProceduresToCreate = containerState?.harvestingProcedures?.filter((updatedProcedure) =>
      !containerHarvestingProcedures?.some((existingProcedure) =>
        updatedProcedure.type === existingProcedure.type
      )
    );
    const harvestingProceduresToDelete = containerHarvestingProcedures?.filter((existingProcedure) =>
      !containerState?.harvestingProcedures?.some((updatedProcedure) =>
        existingProcedure.type === updatedProcedure.type
      )
    );

    const harvestingProceduresToUpdate = containerHarvestingProcedures?.reduce((acc, originalProcedure) => {
      const procedureToUpdate = containerState?.harvestingProcedures?.find(procedureToUpdate =>
        procedureToUpdate['type'] === originalProcedure['type']
      );

      if (!procedureToUpdate) return acc;

      const updatedStorageAdvice = procedureToUpdate.type === SINGLE_HARVEST_ROOTED
        ? containerState?.rootedAdvice?.value
        : containerState?.cutAdvice?.value;


      const updatedProcedure = { ...procedureToUpdate, storageAdvice: updatedStorageAdvice };
      if (updatedProcedure) {
        const propertiesOfOriginalProcedure = Object.keys(originalProcedure);
        const isDifferent = propertiesOfOriginalProcedure.some(prop =>
          originalProcedure[prop] !== updatedProcedure[prop]
        );

        if (isDifferent) {
          acc.push(updatedProcedure);
        }
      }
      return acc;
    }, []);

    return {
      harvestingProceduresToCreate,
      harvestingProceduresToDelete,
      harvestingProceduresToUpdate
    };
  }

  const copyTextToClipboard = (text) => {
    if (!navigator.clipboard) {
      fallbackCopyTextToClipboard(text);
      return;
    }
    navigator.clipboard.writeText(text).then(
      function () {
        notifySuccess('Copied to clipboard.');
      },
      function () {
        notifyError('Could not copy id to clipboard');
      }
    );
  };

  const fallbackCopyTextToClipboard = (text) => {
    var textArea = document.createElement('textarea');
    textArea.value = text ? text : '';

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (!successful) {
        notifyError('Could not copy id to clipboard');
      }
    } catch {
      notifyError('Could not copy id to clipboard');
    }

    document.body.removeChild(textArea);
  };

  const getFarmHandInputValue = () => {
    if (!containerState['farmHandName']?.value) return '';

    return containerState['farmHandName']?.value;
  }

  const getLocation = useCallback((place) => {
    var number = place.address_components.find(element => element.types.includes('street_number'));
    var street = place.address_components.find(element => element.types.includes('route'));
    var postalCode = place.address_components.find(element => element.types.includes('postal_code'));
    var city = place.address_components.find(element => element.types.includes('locality') || element.types.includes('postal_town'));
    var country = place.address_components.find(element => element.types.includes('country'));

    setContainerState((prev) => ({
      ...prev,
      ['location']: {
        ...prev.location,
        value: {
          ...prev.location.value,
          number: number ? number.long_name : '',
          street: street ? street.long_name : '',
          postalCode: postalCode ? postalCode.long_name : '',
          city: city ? city.long_name : '',
          country: country ? country.long_name : '',
        }
      },
    }));
    setLocationString(place.formatted_address);
  }, []);

  const handleAbort = (event) => {
    event?.persist();
    navigateToDetails();
  }

  const handleAddHarvestProcedure = (event) => {
    event.persist();
    const selectedHarvestingProcedureToAdd = document.querySelector('#edit-container_harvestingProcedures')?.value;
    if (containerState?.harvestingProcedures?.find(i => i.type === selectedHarvestingProcedureToAdd)) {
      return;
    }
    const harvestingProcedure = harvestingProcedureTypes?.find((option) => option === selectedHarvestingProcedureToAdd);
    const newHarvestingProcedure = {
      storageAdvice: harvestingProcedure === SINGLE_HARVEST_ROOTED ? containerState?.rootedAdvice?.value : containerState?.cutAdvice?.value,
      type: harvestingProcedure
    }

    const newHarvestingProcedures = [...containerState.harvestingProcedures ?? [], newHarvestingProcedure];
    setContainerState((prev) => ({
      ...prev,
      ['harvestingProcedures']: newHarvestingProcedures,
    }));
  };

  const handleRemoveHarvestProcedure = (harvestingProcedure) => {
    const newSelectedHarvestingProcedures = containerState?.harvestingProcedures.filter((procedure) => procedure.type !== harvestingProcedure.type);
    setContainerState((prev) => ({
      ...prev,
      ['harvestingProcedures']: newSelectedHarvestingProcedures,
    }));
  }

  const navigateToDetails = () => {
    const updatedContainer = accessibleContainers.find(c => c.id === container?.id);
    if (!updatedContainer) return;
    navigate(`/management/containers/${updatedContainer.id}/details`);
  }

  const onChange = (event) => {
    event.persist();

    const key = event.currentTarget.name;
    const newValue = event.currentTarget.value;

    setContainerState((prev) => ({
      ...prev,
      [key]: {
        ...containerState[key],
        value: newValue,
        isValid: containerState[key]?.validate(newValue),
        touched: true,
      },
    }));
  };

  const onCheckFarmHand = async (event) => {
    setProbingFarmHand(true);
    event.persist();
    const farmHandName = event?.currentTarget?.value;

    if (farmHandName == containerState['farmHandName'].initialValue) {
      setProbingFarmHand(false);
      setContainerState((prev) => ({
        ...prev,
        ['farmHandInstallationId']: {
          ...containerState['farmHandInstallationId'],
          value: prev.farmHandInstallationId.initialValue ?? prev.farmHandInstallationId.value,
          touched: false,
        },
      }));
      return;
    }

    if (!farmHandName) {
      setProbingFarmHand(false);
      setContainerState((prev) => ({
        ...prev,
        ['farmHandInstallationId']: {
          ...containerState['farmHandInstallationId'],
          value: '',
          isValid: true,
          touched: true,
        },
      }));
      return;
    }

    let farmHandInstallationId;
    try {
      const result = await FarmhandInstallationsApiService.getFarmHandInstallationId(farmHandName);
      farmHandInstallationId = result.data?.data?.id;

    } catch (error) {
      setProbingFarmHand(false);
      setContainerState((prev) => ({
        ...prev,
        ['farmHandName']: {
          ...containerState['farmHandName'],
          value: initialContainerState.farmHandName?.value,
          isValid: containerState['farmHandName']?.validate(farmHandName),
          touched: true,
        },
        ['farmHandInstallationId']: {
          ...containerState['farmHandInstallationId'],
          value: initialContainerState.farmHandInstallationId?.value,
          isValid: containerState['farmHandInstallationId']?.validate(farmHandName),
          touched: true,
        },
      }));
      let farmerAppError = error?.response?.data;
      if (!farmerAppError) {
        console.error("Unexpected error while checking farmhand id.", error);
        notifyError(<>We have encountered an issue on our side while checking the given farmhand id. You can still continue updating the container and add the farmhand installation later. If the error still exists, please contact {HELP_DESK_LINK}.</>);
        return;
      };

      if (farmerAppError.code === 404) {
        notifyError('The given farmhand installation does not exist. Please select another farmhand installation and try again.');
        return;
      }

      if (farmerAppError.code === 500) {
        notifyError(<>Cannot check if the given farmhand id exists. You can still continue updating the container and add the farmhand installation later. If the error persists, please contact {HELP_DESK_LINK}.</>);
        return;
      };

      notifyError(<>We have encountered an issue on our side while checking the given farmhand id. You can still continue updating the container and add the farmhand installation later. If the error still exists, please contact {HELP_DESK_LINK}.</>);
      return;
    }

    let containerId;
    try {
      const result = await FarmhandInstallationsApiService.getContainerForFarmHandInstallationId(farmHandInstallationId);
      containerId = result?.data?.data?.id;
      if (containerId) {
        setProbingFarmHand(false);
        notifyError('The given farmhand id is already in use. Please try a different farmhand id.');
        return;
      }
    } catch (error) {
      console.error("Unexpected error while checking farmhand id.", error);
      setProbingFarmHand(false);
      notifyError(<>We have encountered an issue on our side while checking the given farmhand id. You can still continue updating the container and add the farmhand installation later. If the error still exists, please contact {HELP_DESK_LINK}.</>);
      return;
    }
    if (farmHandName == '' || farmHandInstallationId) {
      setProbingFarmHand(false);
      setContainerState((prev) => ({
        ...prev,
        ['farmHandName']: {
          ...containerState['farmHandName'],
          value: farmHandName,
          isValid: containerState['farmHandName']?.validate(farmHandName),
          touched: true,
        },
        ['farmHandInstallationId']: {
          ...containerState['farmHandInstallationId'],
          value: farmHandInstallationId,
          isValid: true,
          touched: true,
        },
      }));
      notifySuccess('Farmhand installation is available.');
      return;
    }
    setProbingFarmHand(false);
    notifyError(<>We have encountered an issue on our side while checking the given farmhand id. Please contact {HELP_DESK_LINK}.</>);
  };

  const onWeighingCameraStateChange = (event) => {
    const newState = event.target.checked;
    setContainerState((prevState) => ({
      ...prevState,
      weighingCameraSettings: {
        ...prevState.weighingCameraSettings,
        enabled: newState,
      }
    }));
  };

  const onRobotStateChange = (event) => {
    const newState = event.target.checked;
    setContainerState((prevState) => ({
      ...prevState,
      robotSettings: {
        ...prevState.robotSettings,
        enabled: newState,
      }
    }));
  };

  const handlePlacePicked = useCallback((place) => {
    if (!place?.geometry?.location) return;

    getLocation(place);
    const latlng = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    setLocation(latlng);
    setContainerState((prev) => ({
      ...prev,
      ['location']: {
        ...prev.location,
        value: {
          ...prev.location.value,
          latitude: latlng.lat,
          longitude: latlng.lng,
        }
      },
    }));
  }, [getLocation]);

  const handleMapLocationClick = useCallback(async (latlng) => {
    try {
      const response = await geocodeLatLng(latlng);
      if (response.results[0]) {
        getLocation(response.results[0]);
        setLocation(latlng);
        setContainerState((prev) => ({
          ...prev,
          ['location']: {
            ...prev.location,
            value: {
              ...prev.location.value,
              latitude: latlng.lat,
              longitude: latlng.lng,
            }
          },
        }));
      } else {
        console.log("No results found");
      }
    } catch (error) {
      notifyError(<>We have encountered an issue. Could you please close the location popup and try again. If the error still exists, please contact the support team {HELP_DESK_LINK}.</>);
      console.error(error);
    }
  }, [getLocation, notifyError]);

  const renderLocationContainer = () => {
    return <Fragment>
      <AutoComplete locationPicked={handlePlacePicked}
        currentLocation={locationString}
        closeLocation={() => setOpenLocation(false)} />
      <div className='location-map-container'>
        {location && (
          <GoogleLocationMap
            center={location}
            onLocationClick={handleMapLocationClick}
            onError={() => notifyError(<>We have encountered an issue. Could you please close the location popup and try again. If the error still exists, please contact the support team {HELP_DESK_LINK}.</>)}
          />
        )}
      </div>
    </Fragment>
  };

  const compareSettings = (settings1, settings2) => {
    // First, check if the objects have the same number of keys
    if (Object.keys(settings1).length !== Object.keys(settings2).length) {
      return false;
    }

    // Then, loop through each key in obj1 and check if the value in obj2
    // matches the value in obj1 for that key
    for (const key of Object.keys(settings1)) {
      if (settings1[key] !== settings2[key]) {
        return false;
      }
    }

    // If all the keys and values match, then the objects are identical
    return true;
  };

  const shouldConfirmBeDisabled = () => {
    const oldLocation = containerState.location.initialValue;
    const newLocation = containerState.location.value;
    const singleCutProcedure = containerState?.harvestingProcedures?.find(p => p.type === SINGLE_HARVEST_CUT);
    const initialSingleCutProcedure = containerHarvestingProcedures?.find(p => p.type === SINGLE_HARVEST_CUT);

    const singleCutAdvice = singleCutProcedure?.storageAdvice ?? '';

    const rootedProcedure = containerState?.harvestingProcedures?.find(p => p.type === SINGLE_HARVEST_ROOTED);
    const initialRootedProcedure = containerHarvestingProcedures?.find(p => p.type === SINGLE_HARVEST_ROOTED);
    const singleRootedAdvice = rootedProcedure?.storageAdvice ?? '';
    const multiCutProcedure = containerState?.harvestingProcedures?.find(p => p.type === MULTI_HARVEST_CUT);
    const initialMultiCutProcedure = containerHarvestingProcedures?.find(p => p.type === MULTI_HARVEST_CUT);
    const multiCutAdvice = multiCutProcedure?.storageAdvice ?? '';
    let shouldBeDisabled = true;
    if (!compareSettings(oldLocation, newLocation)) {
      shouldBeDisabled = false;
    }
    if (containerState?.weighingCameraSettings?.initialEnabled != containerState?.weighingCameraSettings?.enabled && containerState?.weighingCameraSettings?.enabled != null) {
      shouldBeDisabled = false;
    }
    if (containerState?.robotSettings?.initialEnabled != containerState?.robotSettings?.enabled && containerState?.robotSettings?.enabled != null) {
      shouldBeDisabled = false;
    }
    if ((containerState?.description?.value != containerState?.description?.initialValue)) {
      shouldBeDisabled = false;
    }

    const initialHarvestingProcedureTypes = (containerHarvestingProcedures  ?? []).map(p => p.type);
    const currentHarvestingProcedureTypes = (containerState.harvestingProcedures ?? []).map(p => p.type);

    const typesAreEqual =
    initialHarvestingProcedureTypes.length === currentHarvestingProcedureTypes.length &&
    initialHarvestingProcedureTypes.every(type => currentHarvestingProcedureTypes.includes(type))

    if (!typesAreEqual) {
      shouldBeDisabled = false;
    }

    if ((singleCutProcedure || multiCutProcedure) && containerState?.cutAdvice?.initialValue !== containerState?.cutAdvice?.value) {
      shouldBeDisabled = false;
    }

    if (rootedProcedure && containerState?.rootedAdvice?.initialValue !== containerState?.rootedAdvice?.value) {
      shouldBeDisabled = false;
    }

    if ((singleCutAdvice !== containerState?.cutAdvice?.value) && singleCutProcedure) {
      if ((!containerState?.cutAdvice?.value && !initialSingleCutProcedure?.storageAdvice)) {
        shouldBeDisabled = shouldBeDisabled ? true : false;

      } else {
        shouldBeDisabled = false;
      }
    }

    if (singleRootedAdvice !== containerState?.rootedAdvice?.value && rootedProcedure) {

      if ((!containerState?.rootedAdvice?.value && !initialRootedProcedure?.storageAdvice)) {
        shouldBeDisabled = shouldBeDisabled ? true : false;
      } else {
        shouldBeDisabled = false;
      }
    }

    if (multiCutAdvice !== containerState?.cutAdvice?.value && multiCutProcedure) {
      if ((!containerState?.cutAdvice?.value && !initialMultiCutProcedure?.storageAdvice)) {
        shouldBeDisabled = shouldBeDisabled ? true : false;
      } else {
        shouldBeDisabled = false;
      }
    }

    if (containerState?.farmHandInstallationId?.value !== containerState?.farmHandInstallationId?.initialValue) {
      if (containerState?.farmHandInstallationId?.value || containerState?.farmHandInstallationId?.initialValue) {
        shouldBeDisabled = false;
      }
    }

   return shouldBeDisabled;
  }

  const onSave = async () => {
    if (!container) return;
    try {
      const oldLocation = containerState.location.initialValue;
      const newLocation = containerState.location.value;
      const locationHasChanged = !compareSettings(oldLocation, newLocation);
      const containerPropertiesHaveChanged = (containerState?.description?.value !== containerState?.description?.initialValue) || (containerState?.cutAdvice.value !== containerState?.cutAdvice.initialValue || containerState?.rootedAdvice.value !== containerState?.rootedAdvice.initialValue);
      setLocalLoading(true);
      if (locationHasChanged || containerPropertiesHaveChanged) {
        const containerData = {
          description: containerState.description.value,
          street: containerState.location.value.street,
          number: containerState.location.value.number,
          postalCode: containerState.location.value.postalCode,
          city: containerState.location.value.city,
          country: containerState.location.value.country,
          latitude: containerState.location.value.latitude,
          longitude: containerState.location.value.longitude,
          isVirtual: containerState.isVirtual.value
        };
        try {
          await ContainersApiService.updateContainer(currentTenant?.id, container?.id, containerData);
        } catch {
          notifyError(<>We have encountered a problem while updating the container. Please contact the support team {HELP_DESK_LINK}.</>, 'management/containers');
          return;
        }
      }

      const {
        harvestingProceduresToCreate,
        harvestingProceduresToDelete,
        harvestingProceduresToUpdate
      } = analyzeHarvestingProceduresUpdate();

      let proceduresError = false;
      try {
        for (const p of (harvestingProceduresToCreate ?? [])) {
          await HarvestingProceduresApiService.createHarvestingProcedure(
            currentTenant?.id,
            containerId,
            {
              type: p.type,
              storageAdvice: p.type === SINGLE_HARVEST_ROOTED
                ? containerState?.rootedAdvice?.value
                : containerState?.cutAdvice?.value
            }
          );
        }

        for (const p of (harvestingProceduresToUpdate ?? [])) {
          const original = (containerHarvestingProcedures ?? []).find(o => o.type === p.type);

          await HarvestingProceduresApiService.updateHarvestingProcedure(
            currentTenant?.id,
            containerId,
            {
              ...(original ? { id: original.id } : {}),
              type: p.type,
              storageAdvice: p.type === SINGLE_HARVEST_ROOTED
                ? containerState?.rootedAdvice?.value
                : containerState?.cutAdvice?.value
            }
          );
        }

        for (const p of (harvestingProceduresToDelete ?? [])) {
          await HarvestingProceduresApiService.deleteHarvestingProcedure(
            currentTenant?.id,
            containerId,
            p.id
          );
        }
      } catch (e) {
        console.error("Error while saving harvesting procedures: ", e);
        proceduresError = true;
      }
      try {
        if (containerState?.farmHandName?.value != containerState?.farmHandName?.initialValue && containerState?.farmHandName?.value !== (containerState?.farmHandName?.initialValue ?? '')) {

          let farmHandInstallationId;
          if (!containerState?.farmHandName.value && containerState?.farmHandName?.initialValue) {

            const result = await FarmhandInstallationsApiService.getFarmHandInstallationId(containerState?.farmHandName?.initialValue);
            farmHandInstallationId = result.data?.data?.id;

            if (farmHandInstallationId) {
              const farmHandPromise = FarmhandInstallationsApiService.unassignFarmHandInstallationFromContainer(currentTenant?.id, container?.id, farmHandInstallationId);
              farmHandPromises.push(farmHandPromise);
            }
          } else if (containerState?.farmHandName?.value) {
            const result = await FarmhandInstallationsApiService.getFarmHandInstallationId(containerState?.farmHandName?.value);
            farmHandInstallationId = result?.data?.data?.id;
            const farmHandPromise = FarmhandInstallationsApiService.assignFarmHandInstallationToContainer(currentTenant?.id, container?.id, {
              farmHandInstallationId
            }).catch(() => {
              console.error("Farmhand installation already in use.");
              farmHandError = true;
            });
            farmHandPromises.push(farmHandPromise);
          }
        }
      } catch {
        farmHandError = true;
      }

      let allIsSuccessful = true;
      try {
        await Promise.all([...farmHandPromises]);

        if (proceduresError || farmHandError || !allIsSuccessful) {
          notifyError(<>The container has been updated, but some settings could not be applied. Please check the container and try to update again. If the error persists, contact our team at {HELP_DESK_LINK}.</>, 'management/containers');
        } else if (cameraError) {
          notifyError(<>The container has been updated, but some weghing camera settings could not be applied. Please check the container and camera settings and try to update again. If the error persists, contact our team at {HELP_DESK_LINK}.</>, 'management/containers');

        } else {
          notifySuccess('Successfully updated container.');
        }
        setAccessibleContainers(currentTenant);
      } catch (error) {
        console.error("Unexpected error while updateing container. ", error);
        allIsSuccessful = false;
      }

      if (containerState?.weighingCameraSettings?.initialEnabled != containerState?.weighingCameraSettings?.enabled) {
        try {
          await DevicesApiService.updateDeviceState(container?.tenantId, container?.id, container?.weighingCameraId, containerState?.weighingCameraSettings?.enabled ? 1 : 0);
        } catch (error) {
          if (error === 'Not found') {
            notifyError(<>We have encountered an issue while updating the camera state. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team {HELP_DESK_LINK}.</>);
          }
          else {
            notifyError(<>We have encountered an issue on our side while saving the new weighing camera settings. Please contact support team {HELP_DESK_LINK}.</>);
          }
          console.error(error);
          cameraError = true;
        }
      }

      if (containerState?.robotSettings?.initialEnabled != containerState?.robotSettings?.enabled) {
        try {
          await DevicesApiService.updateDeviceState(container?.tenantId, container?.id, container?.robotId, containerState?.robotSettings?.enabled ? 1 : 0);
        } catch (error) {
          if (error === 'Not found') {
            notifyError(<>We have encountered an issue while updating the robot state. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team {HELP_DESK_LINK}.</>);
          }
          else {
            notifyError(<>We have encountered an issue on our side while saving the new robot state. Please contact support team {HELP_DESK_LINK}.</>);
          }
          console.error(error);
        }
      }
    } catch (error) {
      console.error("Unexpected error while updating container: ", error);
      notifyError(<>We have encountered an unexpected error. Please check the container and try to update again. If the error persists, contact our team at {HELP_DESK_LINK}.</>, 'management/containers');
    } finally {
      await setAccessibleContainers(currentTenant);
      setSaveDone(true);
    }
  };

  return (localLoading || !isAllRequiredDataPresent() || harvestingProceduresLoading) ? <Loading fullScreen={true} /> : <>
    <div className="edit-container-container-wrapper">
      <div className="edit-container-card-container">
      <div className="edit-container-card">
        <div className='edit-container-title'>General Information</div>
        <div className="container-name-icon"><i className="fa-solid fa-signature"></i></div>
        <div className="container-name-label">Name</div>
        <input className='container-name-input disabled-italic' type='text' disabled value={containerState?.name?.value} />
        <div className="container-location-icon"><i className="fa-solid fa-location-dot"></i></div>
        <div className="container-location-label">Location</div>
        <div className="container-location-details">{containerState?.location?.value?.latitude && containerState?.location?.value?.longitude ? (<>{containerState?.location?.value?.street} {containerState?.location?.value?.number}, {containerState?.location?.value?.postalCode} {containerState?.location?.value?.city}
          <br />
        {containerState?.location?.value?.country}</> ) : ("")}
        </div>
        <div className="container-location-popup-button" onClick={() => { setOpenLocation(true); }}><i className="fa-solid fa-pen"></i></div>
        <Devices
          devices={[
            {
              icon: 'fas fa-balance-scale',
              label: 'Scale',
              value: container?.scaleId.toLowerCase(),
              className: 'container-management-details-scale',
              copy: false,
            },
            {
              icon: 'fas fa-print',
              label: 'Label Printer',
              value: container?.labelPrinterId.toLowerCase(),
              className: 'container-management-details-label-printer',
              onCopy: () => copyTextToClipboard(container?.labelPrinterId)

            },
            {
              icon: 'fas fa-camera',
              label: 'Bird\'s Eye-View Camera',
              value: container?.weighingCameraId.toLowerCase(),
              className: 'container-management-details-weighing-camera',
              statusIcon: containerState?.weighingCameraSettings.enabled ? 'fas fa-circle' : 'far fa-circle',
              copy: false,
              edit: true,
              deviceState: containerState?.weighingCameraSettings.enabled,
              onChange: (event) => onWeighingCameraStateChange(event)
            },
            {
              icon: 'fas fa-robot',
              label: 'Robot',
              value: container?.robotId.toLowerCase(),
              className: 'container-management-details-robot',
              statusIcon: containerState?.robotSettings.enabled ? 'fas fa-circle' : 'far fa-circle',
              copy: false,
              edit: true,
              deviceState: containerState?.robotSettings.enabled,
              onChange: (event) => onRobotStateChange(event)
            }
          ]}

        />
        <div className="container-misc-title">Miscellaneous</div>
        <div className="container-farmhand-icon"><i className="fa-solid fa-tractor"></i></div>
        <div className="container-farmhand-label">Farmhand ID</div>
        <input className={probingFarmHand ? 'container-farmhand-id-input disabled-italic' : "container-farmhand-id-input"} type='text' name='farmHandName' value={probingFarmHand ? `Checking FarmHand Id "${containerState['farmHandName']?.value}"` : getFarmHandInputValue()} onChange={onChange} onBlur={onCheckFarmHand} />
        {containerHarvestingProcedures && <><div className={containerState?.harvestingProcedures?.some(hp => hp.type.includes("Root")) ? "container-root-advice-icon" : "container-root-advice-icon disable"}><i className="fa-solid fa-box-archive"></i></div>
          <div className={containerState?.harvestingProcedures?.some(hp => hp.type.includes("Root")) ? "container-root-advice-label" : "container-root-advice-label disable"}>Storage Advice Text for Root Produces</div>
          <textarea
            className="container-root-advice-input"
            disabled={!containerState?.harvestingProcedures?.some(hp => hp.type.includes("Root"))}
            name="rootedAdvice"
            placeholder={containerState?.rootedAdvice?.placeholder}
            value={containerState?.rootedAdvice?.value}
            onChange={onChange}
          />
          <div className={containerState?.harvestingProcedures?.some(hp => hp.type.includes("Cut")) ? "container-cut-advice-icon" : "container-cut-advice-icon disable"}><i className="fa-solid fa-box-archive"></i></div>
          <div className={containerState?.harvestingProcedures?.some(hp => hp.type.includes("Cut")) ? "container-cut-advice-label" : "container-cut-advice-label disable"}>Storage Advice Text for Cut Produces</div>
          <textarea className="container-cut-advice-input" disabled={!containerState?.harvestingProcedures?.some(hp => hp.type.includes("Cut"))} name='cutAdvice' placeholder={containerState?.cutAdvice?.placeholder} value={containerState?.cutAdvice?.value ?? ''} onChange={onChange} /></>}
        <div className="container-description-icon"><i className="fa-regular fa-comment-dots"></i></div>
        <div className="container-description-label">Description</div>
        <textarea className="container-description-input" value={containerState?.description?.value ?? ''} onChange={onChange} name='description' />
        <div className="container-procedures-icon"><i className="fa-solid fa-wheat-awn"></i></div>
        <div className="container-procedures-label">Harvesting Procedures</div>
        <select
          id='edit-container_harvestingProcedures'
          className='container-procedures-dropdown'
        >
          {harvestingProcedureTypes?.map((harvestingProcedure, index) => (
            <option key={harvestingProcedure + index} value={harvestingProcedure} disabled={containerState.harvestingProcedures?.some(type => type?.type === harvestingProcedure)} >
              {separateOnCapitalLetters(harvestingProcedure)}
            </option>
          ))}
        </select>
        <button
          className='container-procedures-add-button'
          onClick={handleAddHarvestProcedure}
        >
          <i className='fa-solid fa-plus' ></i>
        </button>
        {containerHarvestingProcedures && <div className='container-procedures-list'>
          {containerState?.harvestingProcedures?.map((option, index) => (
            <div key={option?.value ?? '' + index} className='container-procedures-item' >
              <span className="container-procedure-name">
                {separateOnCapitalLetters(option?.type ?? '')}
              </span>
              <i className="fa-solid fa-circle-minus container-procedures-remove-icon" onClick={() => handleRemoveHarvestProcedure(option)} ></i>
            </div>
          ))}
        </div>}
      </div>
      </div>
      <StatusBarButton
        statusSlot={3}
        label='Abort'
        clickHandler={handleAbort} icon='fas fa-ban' type='inline' />
      <StatusBarButton
        className='seed-card-btn'
        label='Confirm'
        statusSlot={5}
        icon='fas fa-check'
        type='inline'
        reversed={true}
        clickHandler={onSave}
        disabled={probingFarmHand || shouldConfirmBeDisabled()}
      />
    </div>
    <Modal size="xl" centered isOpen={openLocation} className='modal-config'>
      <ModalBody className='location-card-body'>
        <div>{renderLocationContainer()}</div>
      </ModalBody>
      <div>
        <Fragment>
          <Button
            className='location-card-btn'
            label={'Done'}
            clickHandler={async () => {
              setOpenLocation(false);
            }}
            disabled={false}
          />
        </Fragment>
      </div>
    </Modal>
  </>
}

export default Edit;
